/* aw-zoek.js — de zoeker, met het gedrag van de bron.
 *
 * Dit is de tegenhanger van `PredictiveSearch` uit `assets/theme.js` van het
 * bronthema. Alles hieronder is daaruit UITGELEZEN, niet bedacht:
 *
 *   wachttijd        280 ms na de laatste aanslag (`autoCompleteDelay`)
 *   minimum          geen — één letter zoekt al
 *   leegmaken        lopende aanvraag afbreken, resultaten wissen, focus terug
 *   onderbreken      elke nieuwe aanslag breekt de lopende aanvraag af
 *   adres            /search/suggest?q=…&section_id=…&resources[limit]=10
 *                    &resources[limit_scope]=each
 *   leeg verzenden   het formulier verzendt niet
 *   openen           0,2s: waas van 0 naar 1, paneel van translateY(-kophoogte)
 *                    naar 0, tijdcurve cubic-bezier(.645,.045,.355,1)
 *   sluiten          precies het omgekeerde
 *   schuifslot       de pagina staat vast zolang de zoeker open is
 *   maximale hoogte  schermhoogte min de bovenkant van het paneel
 *
 * Horizons eigen zoeker gaat uit zoals zijn wagenlade uitging: verbergen én
 * zijn dialoog netjes sluiten op het ELEMENT. Alleen het venster sluiten laat
 * het schuifslot staan, en dan schuift de pagina daarna niet meer (V48).
 */
(() => {
  if (window.__awZoek) return;
  window.__awZoek = true;

  const SECTIE = 'aw-zoek';
  const WACHT = 280;

  const lade = () => document.querySelector('[data-aw-zoek]');
  const veld = () => document.querySelector('[data-aw-zoek-veld]');
  const bak = () => document.querySelector('[data-aw-zoek-resultaten]');

  /* ── Waar begint de lade ────────────────────────────────────────────────
     Onder de kop, net als bij de bron. Dat is geen vast getal: de meldingsbalk,
     de hoogte van het logo en de plakstand veranderen hem. Dus meten. */
  function meet() {
    const kop = document.querySelector('header-component, .header-wrapper, .header');
    if (!kop) return;
    /* Nooit negatief: is de kop weggescrold, dan hoort de lade tegen de
       bovenkant van het scherm te staan en niet ergens halverwege. Zo stond hij
       eerder los van de kop zodra je naar beneden was gescrold. */
    const onder = Math.max(0, Math.round(kop.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--aw-zoek-top', onder + 'px');
  }

  /* ── Openen en sluiten ──────────────────────────────────────────────────── */
  let open = false;

  function toon() {
    const l = lade();
    if (!l || open) return;
    meet();
    open = true;
    l.hidden = false;
    /* Nog twee keer meten NA het zetten van het slot: `overflow:hidden` kan de
       pagina laten verspringen en dan is de kop ineens ergens anders. Eén meting
       vooraf is daardoor te vroeg. */
    requestAnimationFrame(meet);
    setTimeout(meet, 60);
    /* Twee beeldjes wachten zodat de overgang echt loopt: een element dat in
       hetzelfde beeldje van `hidden` af komt animeert niet. */
    requestAnimationFrame(() => requestAnimationFrame(() => l.classList.add('is-open')));
    document.documentElement.classList.add('aw-zoek-slot');
    setTimeout(() => veld()?.focus(), 60);
  }

  function verberg() {
    const l = lade();
    if (!l || !open) return;
    open = false;
    l.classList.remove('is-open');
    document.documentElement.classList.remove('aw-zoek-slot');
    /* En Horizons eigen slot ook. Zijn zoekvenster kan intussen open zijn
       geweest, en dan staat `scroll-lock` op <html> — precies waardoor de
       pagina na de wagen niet meer schoof (V48). Het venster sluiten is niet
       genoeg; het slot moet er los van weg. */
    horizonUit();
    document.documentElement.removeAttribute('scroll-lock');
    /* Pas verbergen als de beweging klaar is, anders knipt hij weg. */
    setTimeout(() => { if (!open) l.hidden = true; }, 220);
    afbreken();
  }

  /* ── Horizons eigen zoeker onschadelijk maken ─────────────────────────────
     Zijn venster staat op `display:none`, maar `showModal()` zet het toch in de
     bovenste laag en maakt daarmee de HELE pagina erachter inert — je ziet er
     niets van en niets is meer klikbaar, ook ons eigen zoekveld niet. Precies
     dezelfde val als bij de wagen. De vangfase-onderschepping hieronder is niet
     genoeg: zijn luisteraar zit ergens waar wij niet bij kunnen. Dus kijken we
     of zijn venster opengaat en sluiten het meteen weer. */
  function horizonUit() {
    const d = document.querySelector('dialog.search-modal__content');
    if (d && d.open) { try { d.close(); } catch (e) {} }
    /* Op het ELEMENT sluiten waar dat kan: dat is wat zijn slot en zijn
       klemring opruimt. Alleen het venster sluiten laat allebei staan. */
    const comp = document.querySelector('dialog-component#search-modal');
    if (comp && typeof comp.closeDialog === 'function') { try { comp.closeDialog(); } catch (e) {} }
    if (!open) document.documentElement.removeAttribute('scroll-lock');
  }
  function bewaakHorizon() {
    const d = document.querySelector('dialog.search-modal__content');
    if (!d) return false;
    horizonUit();
    new MutationObserver(horizonUit).observe(d, { attributes: true, attributeFilter: ['open'] });
    return true;
  }
  /* ── Het zoekvenster van Horizon HELEMAAL weghalen ────────────────────────
     20 aug. Klacht van de eigenaar: "nadat ik in een menu, search of cart kom
     en eruit, freezet mijn scherm soms — dan kan ik niks meer swipen, alleen
     aanraken."

     Nagespeeld en het is één vaste volgorde: **zoeker openen en sluiten, dán
     het menu openen en sluiten.** Daarna blijft `scroll-lock` op <html> staan
     en dus `overflow: hidden` — je kunt wel tikken, maar niet meer schuiven.
     Zes andere volgordes gaven geen enkel probleem.

     De oorzaak zit in Horizons boekhouding. `utilities.js` houdt een LIJST van
     eigenaren van het slot bij; het slot gaat er pas af als die lijst leeg is.
     En `dialog.js` schrijft zich in met `lockScroll(dialog)` in het volgende
     beeldje ná het openen, en schrijft zich pas weer uit ná zijn sluitanimatie:

         showDialog:  requestAnimationFrame(() => { lockScroll(dialog); ... })
         closeDialog: await onAnimationEnd(dialog); unlockScroll(dialog);

     Wij sloegen zijn venster meteen dicht met `close()`. Die animatie komt dan
     nooit, dus `unlockScroll` draait nooit, en zijn venster blijft voor eeuwig
     als eigenaar in die lijst staan. Zolang er één eigenaar in staat, zet
     Horizon het slot bij ELKE volgende lade weer terug — vandaar dat het menu
     erna de pagina op slot laat.

     Het attribuut zelf weghalen helpt niet: er staat een waarnemer op <html>
     die bij elke wijziging opnieuw synchroniseert en het terugzet. Dat is
     detectie, en die verloor.

     Wat wel werkt, en dat is Horizons eigen uitweg: hij gooit eigenaren weg die
     niet meer in de pagina staan (`pruneDisconnectedScrollLockOwners`). Dus
     halen we zijn zoekvenster gewoon uit de DOM. Wij hebben het niet nodig —
     onze eigen zoeklade heeft het volledig vervangen — en het veroorzaakte
     hiervoor al twee andere problemen (zie de aantekening hieronder over
     `showModal`, die de hele pagina erachter onaanraakbaar maakte).

     Gemeten, zelfde volgorde zoeker -> menu:
       zonder deze reparatie   na het menu: slot=true,  overflow=hidden
       met deze reparatie      na het menu: slot=false, overflow=visible

     Het moet HERHAALD gebeuren: Horizon haalt de kopsectie na het laden nog een
     keer op en giet die met morph.js over de pagina, en dan staat zijn venster
     er zo weer. Zie het geheugen `reference_horizon_hydratie_morph`. */
  function sloopHorizonZoeker() {
    let n = 0;
    document.querySelectorAll('dialog.search-modal__content, dialog-component#search-modal')
      .forEach((e) => { try { e.remove(); n++; } catch (err) {} });
    return n;
  }
  sloopHorizonZoeker();
  new MutationObserver(() => sloopHorizonZoeker()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  document.addEventListener('shopify:section:load', sloopHorizonZoeker);

  /* De oude bewaking blijft staan als vangnet: mocht zijn venster ooit in een
     vorm terugkomen die we niet weghalen, dan wordt hij in elk geval gesloten. */
  if (!bewaakHorizon()) {
    const w = new MutationObserver(() => { if (bewaakHorizon()) w.disconnect(); });
    w.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => w.disconnect(), 10000);
  }

  /* De knop in de kop overnemen in de VANGFASE: Horizons `on:click` staat op
     het element zelf, dus een gewone listener komt te laat. */
  document.addEventListener('click', (e) => {
    const knop = e.target.closest?.('button[aria-label="Search"], [aria-controls*="search-modal"], a[href$="/search"]');
    if (!knop) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    horizonUit();
    toon();
  }, true);

  document.addEventListener('click', (e) => {
    if (e.target.closest?.('[data-aw-zoek-sluit]')) { e.preventDefault(); verberg(); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) verberg(); });

  /* ── Ophalen ────────────────────────────────────────────────────────────── */
  let stop = null;
  let tik = null;

  function afbreken() { stop?.abort(); stop = null; }

  function leegmaken() {
    afbreken();
    const b = bak();
    if (b) b.innerHTML = '';
    veld()?.focus();
  }

  async function haal(term) {
    afbreken();
    stop = new AbortController();
    const url = `${window.Shopify?.routes?.root || '/'}search/suggest`
      + `?q=${encodeURIComponent(term)}`
      + `&section_id=${SECTIE}`
      + `&resources[limit]=10&resources[limit_scope]=each`;
    try {
      const tekst = await fetch(url, { signal: stop.signal, credentials: 'same-origin' }).then((r) => r.text());
      const doos = new DOMParser().parseFromString(tekst, 'text/html');
      const nieuw = doos.querySelector('[data-aw-z-inhoud]');
      const b = bak();
      if (nieuw && b) b.replaceChildren(...nieuw.childNodes);
    } catch (err) {
      /* Een afgebroken aanvraag is geen fout: dat is de bedoeling. */
      if (err.name !== 'AbortError') throw err;
    }
  }

  document.addEventListener('input', (e) => {
    if (!e.target.closest?.('[data-aw-zoek-veld]')) return;
    const term = e.target.value.trim();
    clearTimeout(tik);
    if (term === '') return leegmaken();
    tik = setTimeout(() => haal(term), WACHT);
  });

  /* Leeg verzenden doet de bron niet. */
  document.addEventListener('submit', (e) => {
    const f = e.target.closest?.('#aw-zoekformulier');
    if (f && !veld()?.value.trim()) e.preventDefault();
  });

  /* ── De tabbladen ───────────────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest?.('[data-aw-z-tab]');
    if (!tab) return;
    e.preventDefault();
    const wikkel = tab.closest('[data-aw-z-tabs]');
    if (!wikkel) return;
    const nr = tab.dataset.awZTab;
    wikkel.querySelectorAll('[data-aw-z-tab]').forEach((t) => {
      t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
    });
    wikkel.querySelectorAll('[data-aw-z-paneel]').forEach((p) => {
      p.hidden = p.dataset.awZPaneel !== nr;
    });
  });

  meet();
  addEventListener('resize', meet, { passive: true });
  addEventListener('scroll', () => { if (!open) meet(); }, { passive: true });
})();
